// v3.15-927: Rep Availability Settings — configure working hours, days, timezone,
// default appointment duration, buffer between appointments, and booking slug.
// Powers the public /book/{slug} page via `GET/PATCH /api/appointments/me/settings`.
import { useEffect, useMemo, useState } from 'react';
import { Settings, Copy, CheckCircle, Loader2, ExternalLink, Clock, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

const DURATIONS = [15, 20, 30, 45, 60, 75, 90, 120];
const BUFFERS   = [0, 5, 10, 15, 20, 30, 45, 60];

export default function RepAvailabilitySettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [startH, setStartH]   = useState('09:00');
  const [endH,   setEndH]     = useState('17:00');
  const [days,   setDays]     = useState(['mon','tue','wed','thu','fri']);
  const [tz,     setTz]       = useState('America/New_York');
  const [dur,    setDur]      = useState(30);
  const [buf,    setBuf]      = useState(15);
  const [slug,   setSlug]     = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = useMemo(() => {
    if (!slug) return '';
    return `${window.location.origin}/book/${slug}`;
  }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/appointments/me/settings');
      const s = data?.settings || {};
      setStartH(s.working_hours?.start || '09:00');
      setEndH(s.working_hours?.end || '17:00');
      setDays(s.working_days || ['mon','tue','wed','thu','fri']);
      setTz(s.timezone || 'America/New_York');
      setDur(Number(s.default_appointment_duration || 30));
      setBuf(Number(s.buffer_time_between_appointments || 15));
      setSlug(s.booking_slug || '');
      setSlugDirty(false);
    } catch (e) {
      toast.error('Failed to load availability settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleDay = (d) => {
    setDays((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);
  };

  const normalizeSlug = (v) =>
    (v || '').toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');

  const save = async () => {
    if (!startH || !endH || startH >= endH) {
      toast.error('Working hours: start must be before end');
      return;
    }
    if (days.length === 0) {
      toast.error('Select at least one working day');
      return;
    }
    const payload = {
      working_hours: { start: startH, end: endH },
      working_days: days,
      timezone: tz,
      default_appointment_duration: Number(dur),
      buffer_time_between_appointments: Number(buf),
    };
    if (slugDirty) {
      const cleaned = normalizeSlug(slug);
      if (!cleaned) {
        toast.error('Booking slug must contain letters/numbers');
        return;
      }
      payload.booking_slug = cleaned;
    }
    setSaving(true);
    try {
      await api.patch('/appointments/me/settings', payload);
      toast.success('Availability saved');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Booking link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading availability…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="availability-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Settings className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-900">Rep Availability</h1>
          <p className="text-xs text-slate-500">Controls your public booking page and internal scheduling rules.</p>
        </div>
      </div>

      {/* Public booking link card */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <ExternalLink className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-800">Your Public Booking Link</h2>
        </div>
        {publicUrl ? (
          <div className="flex items-center gap-2">
            <Input
              value={publicUrl}
              readOnly
              className="bg-slate-50 text-xs"
              data-testid="public-booking-url"
            />
            <Button type="button" variant="outline" size="sm" onClick={copyLink} data-testid="copy-booking-link-settings">
              {copied ? <CheckCircle className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
              data-testid="open-booking-link-settings"
            >
              Open
            </a>
          </div>
        ) : (
          <p className="text-xs text-slate-400">Save a booking slug below to activate your public link.</p>
        )}
        <div className="mt-3">
          <Label className="text-[11px] font-medium text-slate-600 block mb-1">Booking Slug</Label>
          <Input
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugDirty(true); }}
            placeholder="e.g. your-name"
            className="max-w-xs"
            data-testid="booking-slug-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">Lowercase letters, numbers, and hyphens only.</p>
        </div>
      </div>

      {/* Working hours & days */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-800">Working Hours</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="text-[11px] font-medium text-slate-600 block mb-1">Start</Label>
            <Input
              type="time"
              value={startH}
              onChange={(e) => setStartH(e.target.value)}
              data-testid="working-start"
            />
          </div>
          <div>
            <Label className="text-[11px] font-medium text-slate-600 block mb-1">End</Label>
            <Input
              type="time"
              value={endH}
              onChange={(e) => setEndH(e.target.value)}
              data-testid="working-end"
            />
          </div>
        </div>
        <Label className="text-[11px] font-medium text-slate-600 block mb-2">Working Days</Label>
        <div className="flex flex-wrap gap-1.5" data-testid="working-days">
          {DAYS.map((d) => {
            const active = days.includes(d.key);
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => toggleDay(d.key)}
                className={`text-xs px-3 py-1.5 rounded border transition ${
                  active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-400'
                }`}
                data-testid={`working-day-${d.key}`}
              >
                {d.label.slice(0, 3)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timezone + duration + buffer */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-800">Scheduling Rules</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] font-medium text-slate-600 block mb-1">Timezone</Label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger data-testid="timezone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-medium text-slate-600 block mb-1">Default Duration (min)</Label>
            <Select value={String(dur)} onValueChange={(v) => setDur(Number(v))}>
              <SelectTrigger data-testid="duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-medium text-slate-600 block mb-1">Buffer Between (min)</Label>
            <Select value={String(buf)} onValueChange={(v) => setBuf(Number(v))}>
              <SelectTrigger data-testid="buffer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BUFFERS.map((b) => <SelectItem key={b} value={String(b)}>{b} min</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-500"
          data-testid="save-availability-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Save Availability
        </Button>
      </div>
    </div>
  );
}
